import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ordersAPI, getErrorMessage } from "../../services/api";
import AdminLayout from "../../components/admin/AdminLayout";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import toast from "react-hot-toast";
import { format } from "date-fns";

const VALID_NEXT_STATUSES: Record<string, string[]> = {
  pending:          ["paid", "cancelled"],
  paid:             ["processing", "cancelled"],
  processing:       ["shipped", "cancelled"],
  shipped:          ["delivered"],
  delivered:        ["return_requested"],
  return_requested: ["returned"],
  returned:         [],
  cancelled:        [],
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  paid: "bg-blue-100 text-blue-800",
  processing: "bg-purple-100 text-purple-800",
  shipped: "bg-indigo-100 text-indigo-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  return_requested: "bg-orange-100 text-orange-800",
  returned: "bg-gray-100 text-gray-600",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [updateForm, setUpdateForm] = useState({ status: "", tracking_number: "", shipping_provider: "" });
  const [updating, setUpdating] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [refundingId, setRefundingId] = useState<string | null>(null);

  useEffect(() => { loadOrders(); }, [filterStatus]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params: any = { page_size: 100 };
      if (filterStatus) params.status = filterStatus;
      const res = await ordersAPI.adminList(params);
      setOrders(res.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  const openUpdate = (order: any) => {
    setSelectedOrder(order);
    setUpdateForm({ status: order.status, tracking_number: order.tracking_number || "", shipping_provider: order.shipping_provider || "" });
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating(true);
    try {
      await ordersAPI.adminUpdateStatus(selectedOrder.id, updateForm);
      toast.success("Order updated!");
      setSelectedOrder(null);
      loadOrders();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUpdating(false);
    }
  };

  const handleRefund = async (order: any) => {
    if (!window.confirm(`Issue full refund of ₹${Number(order.total_amount).toLocaleString("en-IN")} for order #${order.order_number}?\n\nMoney will be returned to customer in 5–7 business days.`)) return;
    setRefundingId(order.id);
    try {
      await ordersAPI.adminRefund(order.id);
      toast.success(`Refund of ₹${Number(order.total_amount).toLocaleString("en-IN")} initiated!`);
      loadOrders();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRefundingId(null);
    }
  };

  return (
    <AdminLayout>
      <Helmet><title>Orders – Admin | Kushi Fashions</title></Helmet>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="input-field py-1.5 text-sm w-44"
        >
          <option value="">All Statuses</option>
          {["pending","paid","processing","shipped","delivered","cancelled","return_requested","returned"].map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
          ))}
        </select>
      </div>

      {/* Update Status Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-5">Update Order #{selectedOrder.order_number}</h2>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                {(VALID_NEXT_STATUSES[selectedOrder.status] || []).length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No further status changes allowed for <span className="font-medium">{selectedOrder.status.replace(/_/g, " ")}</span> orders.</p>
                ) : (
                  <select value={updateForm.status} onChange={(e) => setUpdateForm(f => ({ ...f, status: e.target.value }))} className="input-field">
                    <option value={selectedOrder.status}>{selectedOrder.status.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())} (current)</option>
                    {(VALID_NEXT_STATUSES[selectedOrder.status] || []).map((s) => (
                      <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Number</label>
                <input type="text" value={updateForm.tracking_number} onChange={(e) => setUpdateForm(f => ({ ...f, tracking_number: e.target.value }))} className="input-field" placeholder="Optional" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Provider</label>
                <input type="text" value={updateForm.shipping_provider} onChange={(e) => setUpdateForm(f => ({ ...f, shipping_provider: e.target.value }))} className="input-field" placeholder="e.g. Delhivery, BlueDart" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={updating} className="btn-primary px-6 py-2.5">{updating ? "Updating..." : "Update"}</button>
                <button type="button" onClick={() => setSelectedOrder(null)} className="btn-secondary px-6 py-2.5">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingSpinner className="py-20" size="lg" />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-gray-500">Order #</th>
                  <th className="text-left px-3 py-3 text-gray-500">Customer</th>
                  <th className="text-left px-3 py-3 text-gray-500">Amount</th>
                  <th className="text-left px-3 py-3 text-gray-500">Status</th>
                  <th className="text-left px-3 py-3 text-gray-500">Date</th>
                  <th className="text-right px-5 py-3 text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-800">{order.order_number}</td>
                    <td className="px-3 py-3 text-gray-600">{order.shipping_address?.full_name || "—"}</td>
                    <td className="px-3 py-3 font-medium text-primary-600">₹{Number(order.total_amount).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-3">
                      <span className={`badge ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-600"}`}>
                        {order.status.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-gray-400 text-xs">
                      {format(new Date(order.created_at), "dd MMM yyyy")}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => openUpdate(order)} className="text-primary-600 hover:text-primary-700 font-medium text-xs">
                          Update
                        </button>
                        {["paid","processing","shipped","delivered","return_requested","returned"].includes(order.status) && (
                          <button
                            onClick={() => handleRefund(order)}
                            disabled={refundingId === order.id}
                            className="text-red-500 hover:text-red-700 font-medium text-xs disabled:opacity-50"
                          >
                            {refundingId === order.id ? "Refunding..." : "Refund"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && (
              <div className="text-center py-12 text-gray-400">No orders found</div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
